import { useState, useCallback, useMemo, type ReactElement } from 'react';
import { type NavigateFunction, useNavigate } from 'react-router-dom';
import track, { errorTypes } from 'c9-tracking';
import { useSnackbar } from 'notistack';

import { type TAppContext, useAppContext } from 'context/AppContext';
import { type TConfig, type TEndpointAPIConfig, useConfigContext } from 'context/ConfigContext';

import { addGiftCard } from 'common/async/async-calls';
import { getProductValue } from 'common/helpers/product';

import FormPage, { TFormData } from '../GiftCardForm/GiftCardForm';

/* ============================================ Local Types ============================================ */
type TUseFormData = (
  addGiftCardEndpoint: TEndpointAPIConfig['addGiftCard'],
  appBasePath: TConfig['appBasePath'],
  userEmail: TAppContext['user']['userDetails']['email'],
  navigate: NavigateFunction,
  enqueueSnackbar: (message: string) => void
) => {
  isLoading: boolean;
  initialFormData: TFormData;
  errorMessage: string;
  onSubmit: (data: Required<TFormData>) => void;
  onCancel: () => void;
};

type TUpdateGiftCardPage = () => ReactElement;

/* ============================================ Local Functions ============================================ */
const useFormData: TUseFormData = (addGiftCardEndpoint, appBasePath, userEmail, navigate, enqueueSnackbar) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const initialFormData: TFormData = useMemo(
    () => ({
      type: 'add'
    }),
    []
  );

  const handleOnSubmit: ReturnType<TUseFormData>['onSubmit'] = useCallback(
    (data) => {
      setIsLoading(true);
      setErrorMessage('');

      addGiftCard(addGiftCardEndpoint, {
        key: getProductValue(data.product),
        amount: data.amount,
        startDate: data.startDate,
        endDate: data.endDate,
        campaign: data.campaign
      })
        .then(() => {
          track.interact(userEmail + ' added new giftcard');
          track.form('add-giftcard', [
            { id: 'user', value: userEmail },
            { id: 'key', value: getProductValue(data.product) },
            { id: 'amount', value: data.amount },
            { id: 'startDate', value: data.startDate.toString() },
            { id: 'endDate', value: data.endDate.toString() },
            { id: 'campaign', value: data.campaign }
          ]);
          enqueueSnackbar(`New Gift card created`);
          window.location.href = appBasePath;
        })
        .catch((error: Error) => {
          setErrorMessage(error.message);
          track.fault('Failed to add giftcard', errorTypes.tech, error.message);
        })
        .finally(() => {
          setIsLoading(false);
        });
    },
    [addGiftCardEndpoint, userEmail, enqueueSnackbar, appBasePath]
  );

  const handleOnCancel = useCallback(() => {
    setErrorMessage('');
    navigate('/');
  }, [navigate]);

  return { isLoading, initialFormData, errorMessage, onSubmit: handleOnSubmit, onCancel: handleOnCancel };
};

/* ============================================ Main Component ============================================ */
const AddGiftCardPage: TUpdateGiftCardPage = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const {
    user: {
      userDetails: { email: userEmail }
    }
  } = useAppContext();
  const {
    appBasePath,
    endpointAPI: { addGiftCard: addGiftCardEndpoint }
  } = useConfigContext();

  const { isLoading, initialFormData, errorMessage, onSubmit, onCancel } = useFormData(
    addGiftCardEndpoint,
    appBasePath,
    userEmail,
    navigate,
    enqueueSnackbar
  );

  return (
    <FormPage
      title="Tilføje Gavekort"
      isLoading={isLoading}
      errorMessage={errorMessage}
      initialFormData={initialFormData}
      onSubmit={onSubmit}
      onCancel={onCancel}
    />
  );
};

export default AddGiftCardPage;
